// src/hooks/useJanetQueue.js — Data hooks + action helpers for Janet Training Queue.
//
// Pages use `useJanetQueue(filters)` to render the list, `useJanetQueueCount()` to
// render the sidebar/top-bar badge, and the named actions below to approve/edit/reject.

import { useState, useEffect, useCallback, useRef, useId } from 'react'
import { supabase } from '../lib/supabase'
import { getSessionWithTimeout } from '../lib/authSession'
import { enqueueJanetQueueDecide } from '../lib/janetQueueFlush'

const PAGE_SIZE = 50

// Statuses janet-coordinate.js writes when Janet acts autonomously (internal
// Q&A replies + coordination intake emails sent with no human approval).
// Grouped so the queue page can show them on one "Auto" tab and surface
// failures instead of letting them vanish into the All tab.
export const AUTO_SENT_STATUSES       = ['direct_qa_sent', 'coordination_intake_sent']
export const AUTO_PROCESSING_STATUSES = ['direct_qa_processing', 'coordination_processing']
export const AUTO_ERROR_STATUSES      = ['direct_qa_error', 'coordination_error']
export const AUTO_STATUSES = [
  ...AUTO_SENT_STATUSES,
  ...AUTO_PROCESSING_STATUSES,
  ...AUTO_ERROR_STATUSES,
]
// BADGE_POLL_MS retained as a safety-net fallback only: if the Realtime
// subscription drops (network blip, Supabase hiccup), the badge still
// refreshes via interval. Set to a long interval so it doesn't compete
// with Realtime under normal conditions.
const BADGE_FALLBACK_POLL_MS = 120_000

// ─── List hook ───────────────────────────────────────────────────────────────

export function useJanetQueue(filters = {}) {
  const [items, setItems]     = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState(null)
  const [stats, setStats]     = useState({ pending: 0, approved: 0, rejected: 0, handled: 0, auto: 0, autoErrors: 0 })
  const lastFilters = useRef(filters)

  const fetchList = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      let q = supabase
        .from('janet_draft_queue')
        .select('*')
        .order('received_at', { ascending: false })
        .limit(PAGE_SIZE)

      // status can be a single value or an array (the Auto tab passes the
      // AUTO_STATUSES module constant, so the reference is render-stable).
      if (filters.status) {
        q = Array.isArray(filters.status)
          ? q.in('status', filters.status)
          : q.eq('status', filters.status)
      }
      if (filters.principal_email)  q = q.eq('principal_email', filters.principal_email)
      if (filters.is_known_contact !== undefined) q = q.eq('is_known_contact', filters.is_known_contact)
      // "For me": rows this user must approve — explicitly routed to them
      // (approver_email), or legacy rows with no approver where they're the
      // principal.
      if (filters.approver_email) {
        const em = filters.approver_email
        q = q.or(`approver_email.eq.${em},and(approver_email.is.null,principal_email.eq.${em})`)
      }
      if (filters.search) {
        const s = filters.search.trim()
        if (s) q = q.or(`from_email.ilike.%${s}%,from_name.ilike.%${s}%,subject.ilike.%${s}%`)
      }

      const { data, error: err } = await q
      if (err) throw err
      setItems(data || [])
    } catch (e) {
      setError(e)
    } finally {
      setLoading(false)
    }
  }, [filters.status, filters.principal_email, filters.is_known_contact, filters.search, filters.approver_email])

  // Stats are always global (not filtered) so sidebar counts are stable
  const fetchStats = useCallback(async () => {
    try {
      const [p, a1, a2, r, h, au, ae] = await Promise.all([
        supabase.from('janet_draft_queue').select('id', { head: true, count: 'exact' }).eq('status', 'pending_review'),
        supabase.from('janet_draft_queue').select('id', { head: true, count: 'exact' }).eq('status', 'approved_sent'),
        supabase.from('janet_draft_queue').select('id', { head: true, count: 'exact' }).eq('status', 'edited_sent'),
        supabase.from('janet_draft_queue').select('id', { head: true, count: 'exact' }).eq('status', 'rejected'),
        supabase.from('janet_draft_queue').select('id', { head: true, count: 'exact' }).eq('status', 'handled_externally'),
        supabase.from('janet_draft_queue').select('id', { head: true, count: 'exact' }).in('status', AUTO_SENT_STATUSES),
        supabase.from('janet_draft_queue').select('id', { head: true, count: 'exact' }).in('status', AUTO_ERROR_STATUSES),
      ])
      setStats({
        pending:  p.count || 0,
        approved: (a1.count || 0) + (a2.count || 0),
        rejected: r.count || 0,
        handled:  h.count || 0,
        auto:       au.count || 0,
        autoErrors: ae.count || 0,
      })
    } catch { /* non-fatal */ }
  }, [])

  useEffect(() => { lastFilters.current = filters; fetchList(); fetchStats()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchList, fetchStats])

  // Realtime — refresh list + stats on any janet_draft_queue change.
  // Event → refetch (not local mutation) so statusTab filters stay correct
  // when a row transitions in or out of the current view.
  useEffect(() => {
    const channel = supabase
      .channel('janet-draft-queue-list')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'janet_draft_queue' }, () => {
        fetchList()
        fetchStats()
      })
      .subscribe()
    return () => { supabase.removeChannel(channel) }
  }, [fetchList, fetchStats])

  const refetch = useCallback(() => { fetchList(); fetchStats() }, [fetchList, fetchStats])

  return { items, loading, error, stats, refetch }
}

// ─── Badge count hook — Realtime subscription + fallback poll ────────────────
//
// Previously polled every 30s. Now primary signal is a Supabase Realtime
// subscription on janet_draft_queue that refetches the count on any change.
// The 120s fallback interval is a safety net in case the channel drops;
// under normal conditions Realtime fires long before the fallback.
//
// Requires `janet_draft_queue` to be in the supabase_realtime publication —
// see docs/specs/REALTIME-1-IMPLEMENTATION.md Step 2. Without the Dashboard
// toggle, the subscription is a no-op and the fallback poll carries the UI.

// With `userId`, counts only drafts waiting on that person (approver, or the
// principal when no approver is set) — the phone's badge. Without it, the
// whole queue, as the desktop sidebar has always shown it.
export function useJanetQueueCount({ userId } = {}) {
  const [pending, setPending] = useState(0)
  // Unique channel name per mount — Supabase reuses channels keyed by name,
  // so when multiple components call this hook (TopBar badge + Sidebar badge
  // + InboxCategorySidebar + page-level), they all hit the same channel
  // instance and the second/third subscriber crashes with
  //   "cannot add 'postgres_changes' callbacks for realtime:<name> after subscribe()"
  // useId() gives each mount a stable distinct id, so each gets its own
  // channel and they coexist cleanly.
  const channelId = useId()

  useEffect(() => {
    let alive = true
    const tick = () => {
      let q = supabase
        .from('janet_draft_queue')
        .select('id', { head: true, count: 'exact' })
        .eq('status', 'pending_review')
      if (userId) q = q.or(`approver_user_id.eq.${userId},and(approver_user_id.is.null,principal_user_id.eq.${userId})`)
      return q.then(({ count }) => { if (alive) setPending(count || 0) })
        .catch(() => {})
    }

    // Initial fetch
    tick()

    // Realtime primary: refetch count on any queue change
    const channel = supabase
      .channel(`janet-draft-queue-badge-${channelId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'janet_draft_queue' }, () => {
        if (alive) tick()
      })
      .subscribe()

    // Fallback: 120s interval in case Realtime disconnects silently
    const h = setInterval(tick, BADGE_FALLBACK_POLL_MS)

    return () => {
      alive = false
      clearInterval(h)
      supabase.removeChannel(channel)
    }
  }, [channelId, userId])

  return { pending }
}

// ─── Coordination threads — follow the principal back-and-forth ──────────────
//
// janet-coordinate opens a janet_conversations row per coordination thread:
// state machine (awaiting_principals → awaiting_approval → …/booked) plus a
// principals[] JSONB with per-person status/responded_at/response_summary.
// These two helpers surface that: a batched map for list-row progress chips,
// and a full loader (conversation + saved_emails correspondence) for the
// detail pane tracker.

export function useCoordinationThreads(items) {
  const [map, setMap] = useState({})
  // Stable key so the effect only refires when the visible thread set changes
  const idsKey = [...new Set(items.map(i => i.gmail_thread_id).filter(Boolean))].sort().join(',')

  useEffect(() => {
    // No sync setState here (lint: set-state-in-effect). When idsKey is empty
    // we simply don't fetch; stale map entries are harmless — rows look up
    // only their own gmail_thread_id.
    if (!idsKey) return
    let alive = true
    supabase
      .from('janet_conversations')
      .select('gmail_thread_id, state, principals, topic, chase_count, last_question_sent_at, last_chase_at, booked_event_url, resulting_queue_id')
      .in('gmail_thread_id', idsKey.split(','))
      .then(({ data }) => {
        if (alive && data) setMap(Object.fromEntries(data.map(c => [c.gmail_thread_id, c])))
      })
    return () => { alive = false }
  }, [idsKey])

  return map
}

export async function fetchCoordinationThread(gmailThreadId) {
  if (!gmailThreadId) return null
  const { data: conv } = await supabase
    .from('janet_conversations')
    .select('*')
    .eq('gmail_thread_id', gmailThreadId)
    .maybeSingle()
  if (!conv) return null
  const { data: emails } = await supabase
    .from('saved_emails')
    .select('id, sender_email, sender_name, to_emails, subject, sent_at, body_text')
    .eq('gmail_thread_id', gmailThreadId)
    .order('sent_at', { ascending: true })
    .limit(50)
  return { conv, emails: emails || [] }
}

// ─── Scheduling approvals count — janet_conversations pointer ────────────────
//
// Scheduling approvals don't live in janet_draft_queue — they're
// janet_conversations rows in state 'awaiting_scheduling_approval', surfaced
// via email + Calendar → Janet (CalendarGuardianTab). This count powers a
// pointer banner on the queue page so they aren't invisible from here.
// Simple mount fetch + slow poll; no Realtime (low-churn table).

export function useSchedulingApprovalCount() {
  const [count, setCount] = useState(0)

  useEffect(() => {
    let alive = true
    const tick = () =>
      supabase
        .from('janet_conversations')
        .select('id', { head: true, count: 'exact' })
        .eq('state', 'awaiting_scheduling_approval')
        .then(({ count: c }) => { if (alive) setCount(c || 0) })
        .catch(() => {})
    tick()
    const h = setInterval(tick, BADGE_FALLBACK_POLL_MS)
    return () => { alive = false; clearInterval(h) }
  }, [])

  return count
}

// ─── Action helpers — POST /api/janet-queue-decide with bearer auth ──────────
//
// Resilient call: tries the network first, falls back to the offline
// mutation queue on session-timeout / offline / network errors / retriable
// 5xx responses (mirrors the Compose send pattern). Throws on hard 4xx
// (validation, auth gone, queue row no longer pending) so the UI can
// show a real error rather than queueing something that will never succeed.
//
// Returns one of:
//   { ...endpointResult }                 — succeeded immediately
//   { queued: true, queueId: <local id> } — landed in the offline queue
//
// Callers should toast accordingly. The mutation queue auto-drains on
// 'online' event + tab focus, and the global OfflineStatusBanner shows
// "N syncing" / "N failed" while it's working through the backlog.

async function callDecide(payload) {
  // 1. Offline short-circuit — don't even try the network
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    const queueId = await enqueueJanetQueueDecide(payload)
    return { queued: true, queueId }
  }

  // 2. Session lookup with timeout — wedged Supabase client shouldn't
  //    spin the Approve / Reject button forever (2026-04-24 incident
  //    pattern). On timeout, queue and return.
  const sessionResult = await getSessionWithTimeout(3000)
  if (sessionResult.timedOut) {
    const queueId = await enqueueJanetQueueDecide(payload)
    return { queued: true, queueId, sessionTimeout: true }
  }
  const token = sessionResult.data?.session?.access_token
  if (!token) throw new Error('Not signed in')

  // 3. Try the network. On retriable failures, queue. On hard failures, throw.
  let resp
  try {
    resp = await fetch('/api/janet-queue-decide', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    })
  } catch (err) {
    // Network-level failure — TypeError 'Failed to fetch' or AbortError.
    // Queue and let the user keep moving.
    const queueId = await enqueueJanetQueueDecide(payload)
    return { queued: true, queueId, networkError: err?.message || 'fetch failed' }
  }

  const raw = await resp.text()
  let result = null
  try { result = JSON.parse(raw) } catch { /* not JSON — a platform error page */ }

  // Retriable server failures → queue. Except a 503 the endpoint wrote itself
  // (JSON with an error): that is "couldn't read the Gmail draft; nothing was
  // sent" — replaying it later from the offline queue would send without the
  // person looking again, so it is shown, never queued.
  const deliberate503 = resp.status === 503 && result?.error
  if (!deliberate503 && (resp.status === 429 || resp.status === 504 || (resp.status >= 500 && resp.status < 600))) {
    const queueId = await enqueueJanetQueueDecide(payload)
    return { queued: true, queueId, status: resp.status }
  }

  if (!result) throw new Error(`Server error (${resp.status}) — ${raw.slice(0, 200)}`)
  if (!resp.ok) {
    // Keep the status and body: a 409 on a Gmail-linked draft carries the
    // Gmail version, the stale flag, or the reconciled status.
    const err = new Error(result.error || `Decide failed (${resp.status})`)
    err.status = resp.status
    err.body = result
    throw err
  }
  return result
}

// sendGmailVersion: send the Gmail-edited draft exactly as it is in Gmail.
// acknowledgeNewerReply: the person has read the newer message on the thread.
// Both are only ever set by a person's click on what the server showed them.
export function approveSend(queueId, { sendGmailVersion, acknowledgeNewerReply } = {}) {
  return callDecide({
    queueId, action: 'approve_send',
    ...(sendGmailVersion ? { sendGmailVersion: true } : {}),
    ...(acknowledgeNewerReply ? { acknowledgeNewerReply: true } : {}),
  })
}

export function editAndSend(queueId, { subject, bodyHtml, cc, bcc, editSummary, acknowledgeNewerReply }) {
  return callDecide({ queueId, action: 'edit_send', subject, bodyHtml, cc, bcc, editSummary, ...(acknowledgeNewerReply ? { acknowledgeNewerReply: true } : {}) })
}

export function rejectDraft(queueId, { rejectReason } = {}) {
  return callDecide({ queueId, action: 'reject', rejectReason })
}

export function markHandled(queueId) {
  return callDecide({ queueId, action: 'mark_handled' })
}

export function regenerateDraft(queueId, { userGuidance } = {}) {
  return callDecide({ queueId, action: 'regenerate_draft', userGuidance })
}

export function setPrincipal(queueId, principalEmail) {
  return callDecide({ queueId, action: 'set_principal', principalEmail })
}

// ─── Single-row loader (for deep-link ?id= selection) ────────────────────────

export async function fetchQueueRow(id) {
  const { data, error } = await supabase
    .from('janet_draft_queue')
    .select('*')
    .eq('id', id)
    .single()
  if (error) throw error
  return data
}

// ─── The Janet work item behind a Gmail draft (Drafts tab) ──────────────────
// Null when the draft isn't one of Janet's, or when the link columns aren't
// there yet (migration 20261020) — the Drafts tab then shows nothing extra.

export async function fetchWorkItemForGmailDraft(gmailDraftId) {
  if (!gmailDraftId) return null
  try {
    const { data, error } = await supabase
      .from('janet_draft_queue')
      .select('id, status, subject, gmail_link_state, gmail_thread_id, gmail_mailbox, principal_email, draft_proposed_revision')
      .eq('gmail_draft_id', gmailDraftId)
      .maybeSingle()
    if (error) return null
    return data || null
  } catch { return null }
}

// ─── Source email body loader (full body isn't denormalized to the queue row)

export async function fetchSourceEmail(savedEmailId) {
  if (!savedEmailId) return null
  const { data, error } = await supabase
    .from('saved_emails')
    .select('id, body_text, body_html, to_emails, cc_emails, sent_at')
    .eq('id', savedEmailId)
    .maybeSingle()
  if (error) return null
  return data
}

// ─── Team email list for principal picker ────────────────────────────────────

export async function fetchTeamPrincipals() {
  // Everyone with a connected Gmail token, except Janet.
  const { data: tokens } = await supabase
    .from('user_gmail_tokens')
    .select('user_id, gmail_email')
  const filtered = (tokens || []).filter(t => t.gmail_email?.toLowerCase() !== 'janet@chatrone.com')
  if (filtered.length === 0) return []

  const userIds = filtered.map(t => t.user_id)
  const { data: profs } = await supabase
    .from('user_profiles')
    .select('id, full_name')
    .in('id', userIds)
  const byId = Object.fromEntries((profs || []).map(p => [p.id, p.full_name]))

  return filtered.map(t => ({
    user_id: t.user_id,
    email: t.gmail_email,
    name: byId[t.user_id] || t.gmail_email.split('@')[0],
  }))
}
